import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextButton } from '@modusign/design-system';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import { SearchParams } from '@app/features/externalDocument/components/DocumentListPage/SearchFilter';

import useInstantSearch from '../../hooks/useInstantSearch';
import DocumentName from './DocumentName';

interface InstantSearchDropdownProps {
  isOpen: boolean;
  keyword: string;
  filter: SearchParams['filter'];
  documentRegisteredFilter: boolean;
  onSubmit: (keyword: string) => void;
}

export default function InstantSearchDropdown({
  isOpen,
  keyword,
  filter,
  documentRegisteredFilter,
  onSubmit,
}: InstantSearchDropdownProps) {
  const { debouncedKeyword, data, isLoading, searchLimit } = useInstantSearch({
    keyword,
    enabled: isOpen,
    filter,
    documentRegisteredFilter,
  });

  if (!isOpen || !debouncedKeyword) {
    return null;
  }

  return (
    <Flex
      bg="white"
      border="1px solid"
      borderColor="menuBorderNeutralBase"
      borderRadius="4px"
      boxShadow="0px 2px 6px rgba(0, 0, 0, 0.101961)"
      className="ExternalDocumentSearchInput--dropdown"
      flexDirection="column"
      left="0"
      maxHeight="450px"
      position="absolute"
      top="calc(100% + 4px)"
      width={['min(450px, 100%)', null, '450px']}
      zIndex={1}
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <Box flex="auto" overflow="auto" py={3}>
        {isLoading && (
          <Box height="60px" position="relative">
            <CenterLoadingSpinner size={24} />
          </Box>
        )}
        {data && (
          <DocumentList
            count={data.count || 0}
            documentList={data.externalDocuments}
            searchKeyword={debouncedKeyword}
            searchLimit={searchLimit}
            onSubmit={onSubmit}
          />
        )}
      </Box>
      <Text
        as="div"
        bg="boxBackgroundNeutralSlightlyLight"
        fontSize="sm"
        p={4}
        sx={{
          borderRadius: '0 0 4px 4px',
          borderTop: '1px solid',
          borderTopColor: 'navigationBorderNeutralSlightlyLight',
          width: '100%',
        }}
      >
        <Text color="iconNeutralSlightlyDark" fontSize="xs" mr={1}>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Text>
        상세 검색 결과를 보거나, 필터를 적용하려면 [Enter] 키를 눌러주세요.
      </Text>
    </Flex>
  );
}

function DocumentList({
  count,
  searchLimit,
  documentList,
  searchKeyword,
  onSubmit,
}: {
  documentList?: Array<{
    id: string;
    title: string;
    signers: Array<{ name: string }>;
  }>;
  count: number;
  searchLimit: number;
  searchKeyword: string;
  onSubmit: (keyword: string) => void;
}) {
  if (!documentList) return null;

  if (documentList.length === 0) {
    return (
      <Flex alignItems="center" height="60px" justifyContent="center">
        검색 결과가 없습니다.
      </Flex>
    );
  }

  return (
    <Box as="ul">
      {documentList.map(({ id, title, signers }) => (
        <Box
          as="li"
          key={id}
          sx={{
            '&:hover': {
              bg: 'menuBackgroundNeutralDark',
            },
          }}
        >
          <Link to={`/external-documents/document/${id}`}>
            <DocumentName
              participantNames={signers.map((p) => p.name)}
              searchString={searchKeyword}
              title={title}
            />
          </Link>
        </Box>
      ))}
      {count > searchLimit && (
        <Box
          alignItems="center"
          as="li"
          display="flex"
          justifyContent="center"
          mt={3}
          py={3}
          width="100%"
        >
          <Text color="textNeutralLight" whiteSpace="pre">
            외 {count - searchLimit}개의 검색 결과{' '}
          </Text>
          <TextButton onClick={() => onSubmit(searchKeyword)}>모두 보기</TextButton>
        </Box>
      )}
    </Box>
  );
}
